import { useToastContext } from '@payaca/ambrosia';
import { useDeal } from '@payaca/store/hooks/appState';
import * as invoiceActions from '@payaca/store/invoices/invoicesActions';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';

interface UseCreateInvoiceOptions {
  onSuccess?: (invoiceId: number) => void;
  onError?: (error: string) => void;
}

const useCreateInvoice = (
  projectId: number,
  options: UseCreateInvoiceOptions = {}
) => {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();
  const deal = useDeal(projectId);

  const { project } = useGetProjectByIdOverview(deal?.publicId);

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { pushError } = useToastContext();

  const createInvoice = useCallback(
    (invoiceValue: number, isDeposit?: boolean) => {
      if (isLoading) return;

      const acceptedValue = project?.totals.acceptedValue.value ?? 0;
      const invoicedValue = project?.totals.invoicedValue.value ?? 0;
      const maxValue = acceptedValue - invoicedValue;

      if (invoiceValue <= 0) {
        pushError('Invoice value must be greater than zero');
        onError?.('Invoice value must be greater than zero');
        return;
      }

      if (invoiceValue > maxValue) {
        pushError(`Invoice value exceeds the maximum allowed value`);
        onError?.(`Invoice value exceeds the maximum allowed value`);
        return;
      }

      setIsLoading(true);
      dispatch(
        invoiceActions.requestCreateInvoice(
          {
            dealId: projectId,
            createForUninvoicedDealValue: invoiceValue === maxValue,
            invoiceValue: invoiceValue,
            isDeposit,
          },
          (invoiceId) => {
            setIsLoading(false);
            onSuccess?.(invoiceId);
            // success callback..?
          },
          () => {
            setIsLoading(false);
            pushError(`Something went wrong creating the Invoice`);
            onError?.(`Something went wrong creating the Invoice`);
          }
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'pushError', and 'dispatch'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: queryClient
    [projectId, project, isLoading, queryClient, onSuccess, onError]
  );

  return {
    isLoading,
    createInvoice,
  };
};

export default useCreateInvoice;
